import { z } from 'zod'
import {
    CanonicalCommercialAccountSchema,
    CanonicalCommercialContactSchema,
    PaginatedCommercialAccountsSchema,
    type CanonicalCommercialAccount,
    type CanonicalCommercialContact,
    type PaginatedCommercialAccounts,
} from '@/schemas/commercial-accounts/schema'
import type { CommercialAccountWrite } from '@/schemas/commercial-accounts/write-schema'
import {
    type EventUser,
    EventSchema,
    PaginatedEventsSchema,
    type AccountEvent,
    type CreateEventPayload,
    type DeleteEventPayload,
    type ListEventsParams,
    type PaginatedEvents,
    type PinEventPayload,
    type UpdateEventPayload,
} from '@/schemas/events/schema'

export type {
    AccountEvent,
    CreateEventPayload,
    DeleteEventPayload,
    EventUser,
    ListEventsParams,
    PaginatedEvents,
    PinEventPayload,
    UpdateEventPayload,
} from '@/schemas/events/schema'

import {
    getLittaPublicApiBaseUrl,
    getLittaPublicApiRemoteBaseUrl,
} from '@/lib/api/litta-api-base-url'
import { littaApiFetch } from '@/lib/api/litta-api-fetch'
import {
    BookerCheckSchema,
    type BookerCheck,
} from '@/lib/booking/bookerPortalAccess'

export {
    getLittaApiOverrideUrl,
    getLittaPublicApiBaseUrl,
    getLittaPublicApiRemoteBaseUrl,
    isLittaLocalEnabled,
    isTrustedOverrideUrl,
    setLittaApiOverrideUrl,
    setLittaLocalEnabled,
} from '@/lib/api/litta-api-base-url'

async function fetchJson(input: string, init?: RequestInit) {
    const res = await littaApiFetch(input, init)
    if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${res.statusText} - ${text}`)
    }
    return res.json()
}

function toQuery(params?: object): string {
    if (!params) return ''
    const usp = new URLSearchParams()
    for (const [key, value] of Object.entries(
        params as Record<string, unknown>,
    )) {
        if (value !== undefined && value !== null) {
            usp.append(key, String(value))
        }
    }
    const s = usp.toString()
    return s ? `?${s}` : ''
}

export interface ListCommercialAccountsParams {
    page?: number
    limit?: number
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    relationshipManager?: string
    groupBy?: string
}

export const listCommercialAccounts = async (
    params?: ListCommercialAccountsParams,
): Promise<PaginatedCommercialAccounts> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts${toQuery({ ...params, page: params?.page ?? 1 })}`,
    )
    const result = PaginatedCommercialAccountsSchema.safeParse(data)
    if (!result.success) {
        console.error(result.error, data)
        throw new Error('Invalid data received from commercial accounts API', {
            cause: result.error,
        })
    }
    return result.data
}

export const getCommercialAccountById = async (
    id: string,
): Promise<CanonicalCommercialAccount> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${id}`,
    )
    return CanonicalCommercialAccountSchema.parse(data)
}

export const patchCommercialAccount = async (
    id: string,
    updates: Partial<CommercialAccountWrite>,
): Promise<CanonicalCommercialAccount> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${id}`,
        {
            method: 'PATCH',
            body: JSON.stringify(updates),
        },
    )
    return CanonicalCommercialAccountSchema.parse(data)
}

export const getCommercialAccountContacts = async (
    accountId: string,
): Promise<CanonicalCommercialContact[]> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/contacts`,
    )
    return z.array(CanonicalCommercialContactSchema).parse(data)
}

export interface RelationshipManager {
    id: string
    name: string
    email: string
}

const RelationshipManagerSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
})

export const listRelationshipManagers = async (): Promise<
    RelationshipManager[]
> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/relationship-managers`,
    )
    return z.array(RelationshipManagerSchema).parse(data)
}

export interface PricingTier {
    id: string
    name: string
    skuIdentifier: string | null
    priority: number | null
    active: boolean
    pricingDocumentIds: string[]
}

const PricingTierSchema = z.object({
    id: z.string(),
    name: z.string(),
    skuIdentifier: z.string().nullable(),
    priority: z.number().nullable(),
    active: z.boolean(),
    pricingDocumentIds: z.array(z.string()),
})

export const listPricingTiers = async (): Promise<PricingTier[]> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/pricing-tiers`,
    )
    return z.array(PricingTierSchema).parse(data)
}

export interface WasteReportingModule {
    id: string
    module: string
    reportingIncluded: string[]
    wasteReportingFrequency: string | null
}

const WasteReportingModuleSchema = z.object({
    id: z.string(),
    module: z.string(),
    reportingIncluded: z.array(z.string()),
    wasteReportingFrequency: z.string().nullable(),
})

export const listWasteReportingModules = async (): Promise<
    WasteReportingModule[]
> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/waste-reporting-modules`,
    )
    return z.array(WasteReportingModuleSchema).parse(data)
}

export interface PricingDocument {
    id: string
    documentName: string
}

const PricingDocumentSchema = z.object({
    id: z.string(),
    documentName: z.string(),
})

export const listPricingDocuments = async (): Promise<PricingDocument[]> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/pricing-documents`,
    )
    return z.array(PricingDocumentSchema).parse(data)
}

export interface PricingDocumentFile {
    filename: string
    url: string
}

const PricingDocumentFileSchema = z.object({
    filename: z.string(),
    url: z.string(),
})

/** Fetches a short-lived download URL live from Airtable; synced URLs expire. */
export const getPricingDocumentFile = async (
    documentId: string,
): Promise<PricingDocumentFile> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/pricing-documents/${documentId}/file`,
    )
    return PricingDocumentFileSchema.parse(data)
}

// --- Events ---

export const listEvents = async (
    accountId: string,
    params?: ListEventsParams,
): Promise<PaginatedEvents> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/events${toQuery(params)}`,
    )
    const result = PaginatedEventsSchema.safeParse(data)
    if (!result.success) {
        console.error(result.error, data)
        throw new Error('Invalid data received from account events API', {
            cause: result.error,
        })
    }
    return result.data
}

export const listPinnedEvents = async (
    accountId: string,
): Promise<AccountEvent[]> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/events/pinned`,
    )
    return EventSchema.array().parse(data)
}

export const pinEvent = async (
    accountId: string,
    eventId: string,
    payload: PinEventPayload,
): Promise<AccountEvent> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/events/${eventId}/pin`,
        { method: 'PUT', body: JSON.stringify(payload) },
    )
    return EventSchema.parse(data)
}

/**
 * After ops book on an account: gives the booker Business Portal access (Admin)
 * on it. Failures of the invite itself come back as `failed: true`.
 */
export const ensureBookerPortalAccess = async (
    accountId: string,
    payload: {
        bookedBy?: EventUser
        bookingRef: string
        email: string
        name?: string
    },
): Promise<BookerCheck> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/booker-access`,
        { method: 'POST', body: JSON.stringify(payload) },
    )
    return BookerCheckSchema.parse(data)
}

export const createEvent = async (
    accountId: string,
    payload: CreateEventPayload,
): Promise<AccountEvent> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/events`,
        { method: 'POST', body: JSON.stringify(payload) },
    )
    return EventSchema.parse(data)
}

export const updateEvent = async (
    accountId: string,
    eventId: string,
    payload: UpdateEventPayload,
): Promise<AccountEvent> => {
    const data = await fetchJson(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/events/${eventId}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
    )
    return EventSchema.parse(data)
}

export const deleteEvent = async (
    accountId: string,
    eventId: string,
    payload: DeleteEventPayload,
): Promise<void> => {
    const res = await littaApiFetch(
        `${getLittaPublicApiBaseUrl()}/commercial-accounts/${accountId}/events/${eventId}`,
        {
            method: 'DELETE',
            body: JSON.stringify(payload),
        },
    )
    if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${res.statusText} - ${text}`)
    }
}
